import React from 'react';
import styled from "styled-components";
import Text from "@/components/common/Text";
import Image, {formatImageCMSData} from "@/components/common/Image";
import {getCssSizeValue} from "@/utils/utils";
import {BREAKPOINTS} from "@/styles/themeSettings";
import GridContainer from "@/components/common/GridContainer";
import StructuredText from "@/components/Archive/StructuredText";

const LAYOUTS = {
	PRIMARY: 'primary',
	SECONDARY: 'secondary',
	TERTIARY: 'tertiary'
}

const Container = styled(GridContainer)`
	--end: calc(var(--grid-count) + 1);
	--start: 2;
	row-gap: 0;
    padding-top: ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};
    transition: opacity var(--rgr-duration) var(--ease);
    opacity: 1;
	
	.right { 
		margin-left: auto;
	}
	
	.image { 
        max-height: 100%;
        grid-column: var(--image-start, 1) / var(--image-end, 8);
	}
	
	.content {
        grid-column: var(--content-start, 1) / var(--content-end, 8);
        --content-start: 1;
        --content-end: 9;
	}

    .header {
        grid-column: 1 / var(--end);
        margin-bottom: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
    }

    &.layout-${LAYOUTS.PRIMARY}{
        .image {
            --image-start: 1;
            --image-end: var(--end);
        }

		.content {
            margin-bottom: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
		}
	}

    &.layout-${LAYOUTS.SECONDARY} {
        .image {
            order: 1;
            --image-start: 1;
            --image-end: var(--end);
            margin-bottom: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
        }

		.content {
			order: 2;
		}
	}

    &.layout-${LAYOUTS.TERTIARY}{
        .image{
            --image-start: 1;
            --image-end: var(--end);
			margin-bottom: ${getCssSizeValue(48, BREAKPOINTS.MOBILE)};
			margin-top: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
        }
    }
	
    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.TABLET]}px){
        flex: 0 0 100%;
		grid-template-rows: auto 1fr;
    	min-height: 100svh;
        opacity: ${({ $active }) => $active ? 1 : 0};
        --grid-count: 8;
        padding-top: ${getCssSizeValue(200, BREAKPOINTS.TABLET)};
        padding-bottom: ${getCssSizeValue(100, BREAKPOINTS.TABLET)};

		.content {
            margin-bottom: unset;
		}
		
		.header {
            margin-bottom: unset;
		}
		
        &.layout-${LAYOUTS.PRIMARY} {
            .image {
                margin-top: auto;
                --image-start: 5;
                --image-end: var(--end);
                //max-height: 80%;
            }
            .content {
                --content-start: 1;
                --content-end: 5;
				margin-bottom: unset;
            }
        }
        &.layout-${LAYOUTS.SECONDARY} {
            .image {
                order: 1;
                --image-start: 1;
                --image-end: 5;
				margin-bottom: unset;
            }
            .content {
                order: 2;
                --content-start: 5;
                --content-end: var(--end);
            }
        }
        &.layout-${LAYOUTS.TERTIARY} {
            .header {
                grid-column: 6 / var(--end);
            }
            .image {
                margin-top: auto;
				
                order: 3;
                --image-start: calc(var(--end) - 3);
                --image-end: var(--end);
            }
            .content {
                --content-start: 1;
                --content-end: 6;
                order: 1;
            }
        }
    }

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
        row-gap: var(--grid-gap);
        --grid-count: 15;
        padding:
                ${getCssSizeValue(88, BREAKPOINTS.DESKTOP)}
                var(--paddingX)
                ${getCssSizeValue(88, BREAKPOINTS.DESKTOP)}
                0;
        &.layout-${LAYOUTS.PRIMARY} {
            .image{
                margin-top: auto;
                --image-start: 8;
                --image-end: var(--end);
            }
            .content{
                --content-start: var(--start);
                --content-end: 8;
            }
        }
        &.layout-${LAYOUTS.SECONDARY} {
            .image{
                order: 1;
                --image-start: 1;
                --image-end: 8;
            }
            .content{
                order: 2;
                --content-start: 9;
                --content-end: 16;
            }
        }
        &.layout-${LAYOUTS.TERTIARY} {
            .image{
                order: 2;
                --image-start: 12;
                --image-end: var(--end);
				margin-bottom: 0;
            }
            .content{
                --content-start: 2;
                --content-end: 10;
                order: 1;
            }
        }
    }
`

const Content = styled.div`
	display: flex;
    flex-direction: column;
	row-gap:${getCssSizeValue(20, BREAKPOINTS.DESKTOP)};
	height: 100%;
	overflow-x: visible;
	overflow-y: auto;
	.download-files {
		width: auto;
        padding: unset;
		a {
			padding: unset;
		}
	}

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		overflow: visible;
    }
`

const Year = ({ data, active=false }) => {
	const header = data?.date || ''
	const context = data?.context
	const {
		url,
		alt,
		focalPoint,
		placeholder,
		aspectRatio,
		metaData,
		showMetaData
	} =  formatImageCMSData(context?.cover.image, context?.cover?.showMetaData)
	const layout = LAYOUTS[context?.layout?.toUpperCase()]|| LAYOUTS.PRIMARY
	return (
		<Container $active={active} className={`no-margin layout-${layout}`}>
			{/*<PrimaryLayout header={header} cover={cover} context={context}/>*/}
			<Text color={"white"} className=" header right typo-borna-85">
				{header}
			</Text>
			<Content className="typo-borna-11 content" >
				<StructuredText data={context?.description || null}/>
			</Content>
			<Image
				src={url}
				alt={alt}
				focalPoint={focalPoint}
				placeholder={placeholder}
				showMetaData={showMetaData}
				metaData={metaData}
				style={{ aspectRatio }}
			/>
		</Container>
	);
};

export default Year;
